import type { Bi, SkillId } from '@/sanskrit/lib/sanskrit/types'
import type { Lesson } from '@/sanskrit/lib/content/lesson-types'

import { LESSONS, LESSON_ORDER, getLesson } from '@/sanskrit/lib/content/lessons'

export type MasteryState = 'new' | 'learning' | 'needs-practice' | 'strong' | 'mastered'

export interface ItemState {
    box: number
    due: string
    seen: number
    correct: number
    wrong: number
    streak: number
    last: 'correct' | 'wrong' | null
    lastSeen: string
    skill: SkillId
}

export interface LessonRecord {
    completedAt: string
    score: number
    best: number
    attempts: number
    passed: boolean
}

export interface GameRecord {
    plays: number
    best: number
    level: number
    last: string
}

export interface RevisionMark {
    on: boolean
    at: string
}

/** Removals are remembered for the latest marks only; older ones can no longer come back from an old device. */
export const REVISION_LOG_CAP = 400

export interface Progress {
    version: 1
    xp: number
    streak: { count: number; last: string | null; best: number }
    daily: Record<string, number>
    lessons: Record<string, LessonRecord>
    lastLesson: string | null
    /** When lastLesson was set, so the newer of two devices wins when they sync. */
    lastLessonAt: string | null
    items: Record<string, ItemState>
    skills: Partial<Record<SkillId, { correct: number; total: number; last: string | null }>>
    confusions: Record<string, number>
    recentWords: string[]
    revision: string[]
    /** The latest add or remove of each revision word, so a removal on one device is not undone by another. */
    revisionLog: Record<string, RevisionMark>
    /** XP earned on each device and account (see `xpKey`); `xp` is never below their sum, so syncing never loses XP. */
    xpBy: Record<string, number>
    games: Record<string, GameRecord>
    achievements: string[]
    history: Array<{ at: string; item: string; ok: boolean; skill: SkillId }>
    storiesRead: string[]
}

export const emptyProgress = (): Progress => ({
    version: 1,
    xp: 0,
    streak: { count: 0, last: null, best: 0 },
    daily: {},
    lessons: {},
    lastLesson: null,
    lastLessonAt: null,
    items: {},
    skills: {},
    confusions: {},
    recentWords: [],
    revision: [],
    revisionLog: {},
    xpBy: {},
    games: {},
    achievements: [],
    history: [],
    storiesRead: []
})

/** Intervals (days) for each Leitner box. */
export const BOX_INTERVALS = [0, 1, 2, 4, 8, 16, 32]

export const MAX_BOX = BOX_INTERVALS.length - 1

export const toDay = (date: Date): string => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

export const addDays = (day: string, n: number): string => {
    const [y, m, d] = day.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    date.setDate(date.getDate() + n)
    return toDay(date)
}

export const masteryOf = (item: ItemState | undefined): MasteryState => {
    if (!item || item.seen === 0) return 'new'
    if (item.last === 'wrong') return 'needs-practice'
    if (item.wrong >= 2 && item.correct / item.seen < 0.6) return 'needs-practice'
    if (item.box >= 5 && item.streak >= 3) return 'mastered'
    if (item.box >= 3) return 'strong'
    return 'learning'
}

export const MASTERY_LABEL: Record<MasteryState, Bi & { sa: string }> = {
    new: { sa: 'नूतनम्', hi: 'नया', en: 'New' },
    learning: { sa: 'अभ्यस्यमानम्', hi: 'सीख रहे हैं', en: 'Learning' },
    'needs-practice': { sa: 'पुनरभ्यासः', hi: 'अभ्यास चाहिए', en: 'Needs practice' },
    strong: { sa: 'दृढम्', hi: 'मज़बूत', en: 'Strong' },
    mastered: { sa: 'सिद्धम्', hi: 'सिद्ध', en: 'Mastered' }
}

export interface AnswerEvent {
    item: string
    skill: SkillId
    ok: boolean
    confusions?: string[]
    now?: Date
}

/** Update an item with one answer (Leitner scheduling). */
export const scheduleItem = (prev: ItemState | undefined, ok: boolean, skill: SkillId, now: Date): ItemState => {
    const today = toDay(now)
    const base: ItemState = prev ?? { box: 0, due: today, seen: 0, correct: 0, wrong: 0, streak: 0, last: null, lastSeen: now.toISOString(), skill }
    const box = ok ? Math.min(MAX_BOX, base.box + 1) : Math.max(1, base.box - 2)

    return {
        ...base,
        box,
        due: ok ? addDays(today, BOX_INTERVALS[box]) : today,
        seen: base.seen + 1,
        correct: base.correct + (ok ? 1 : 0),
        wrong: base.wrong + (ok ? 0 : 1),
        streak: ok ? base.streak + 1 : 0,
        last: ok ? 'correct' : 'wrong',
        lastSeen: now.toISOString(),
        skill
    }
}

export const recordAnswer = (p: Progress, e: AnswerEvent): Progress => {
    const now = e.now ?? new Date()
    const stats = p.skills[e.skill] ?? { correct: 0, total: 0, last: null }
    const confusions = { ...p.confusions }

    if (!e.ok && e.confusions) {
        for (const c of e.confusions) confusions[c] = (confusions[c] ?? 0) + 1
    }

    return {
        ...p,
        items: { ...p.items, [e.item]: scheduleItem(p.items[e.item], e.ok, e.skill, now) },
        skills: { ...p.skills, [e.skill]: { correct: stats.correct + (e.ok ? 1 : 0), total: stats.total + 1, last: now.toISOString() } },
        confusions,
        history: [...p.history, { at: now.toISOString(), item: e.item, ok: e.ok, skill: e.skill }].slice(-300)
    }
}

/** Mark items as seen during teaching (moves "new" items to "learning" without counting an answer). */
export const introduceItems = (p: Progress, items: Array<{ item: string; skill: SkillId }>, now = new Date()): Progress => {
    const next = { ...p.items }
    let changed = false

    for (const { item, skill } of items) {
        if (next[item]) continue
        next[item] = { box: 0, due: toDay(now), seen: 0, correct: 0, wrong: 0, streak: 0, last: null, lastSeen: now.toISOString(), skill }
        changed = true
    }

    return changed ? { ...p, items: next } : p
}

export const addXp = (p: Progress, amount: number, now = new Date()): Progress => {
    if (amount <= 0) return p

    const today = toDay(now)
    const yesterday = addDays(today, -1)
    let { count, best } = p.streak
    const last = p.streak.last

    if (last !== today) count = last === yesterday ? count + 1 : 1
    best = Math.max(best, count)

    const daily = { ...p.daily, [today]: (p.daily[today] ?? 0) + amount }
    const keys = Object.keys(daily).sort()
    while (keys.length > 60) delete daily[keys.shift() as string]

    return { ...p, xp: p.xp + amount, streak: { count, best, last: today }, daily }
}

/** The streak shown today: it lapses if neither today nor yesterday had activity. */
export const currentStreak = (p: Progress, now = new Date()): number => {
    const today = toDay(now)
    if (p.streak.last === today || p.streak.last === addDays(today, -1)) return p.streak.count
    return 0
}

export const passMarkOf = (lesson: Lesson): number => lesson.passMark ?? 0.6

export const completeLesson = (p: Progress, lessonId: string, score: number, now = new Date()): { progress: Progress; xp: number; passed: boolean; newAchievements: string[] } => {
    const lesson = getLesson(lessonId)
    if (!lesson) return { progress: p, xp: 0, passed: false, newAchievements: [] }

    const passed = score >= passMarkOf(lesson)
    const prev = p.lessons[lessonId]
    const firstPass = passed && !prev?.passed
    const xp = passed ? (firstPass ? 15 : 5) + (score >= 0.999 ? 5 : 0) : 2

    let next: Progress = {
        ...p,
        lastLesson: lessonId,
        lastLessonAt: now.toISOString(),
        lessons: {
            ...p.lessons,
            [lessonId]: {
                completedAt: now.toISOString(),
                score,
                best: Math.max(prev?.best ?? 0, score),
                attempts: (prev?.attempts ?? 0) + 1,
                passed: passed || Boolean(prev?.passed)
            }
        }
    }

    next = addXp(next, xp, now)
    const before = new Set(next.achievements)
    next = { ...next, achievements: evaluateAchievements(next, score) }
    const newAchievements = next.achievements.filter(a => !before.has(a))

    return { progress: next, xp, passed, newAchievements }
}

export interface UnlockOptions {
    knowsDevanagari: boolean
}

export const isLessonPassed = (p: Progress, id: string): boolean => Boolean(p.lessons[id]?.passed)

/**
 * A lesson opens when the previous required lesson is passed.
 * The taster lesson is always open; Devanagari lessons become optional for learners who already read the script.
 */
export const isLessonUnlocked = (p: Progress, lessonId: string, opts: UnlockOptions): boolean => {
    const lesson = getLesson(lessonId)
    if (!lesson) return false
    if (lesson.taster) return true
    if (opts.knowsDevanagari && lesson.track === 'devanagari') return true

    const index = LESSON_ORDER.indexOf(lessonId)
    if (index <= 0) return true

    for (let j = index - 1; j >= 0; j--) {
        const prev = getLesson(LESSON_ORDER[j])
        if (!prev) continue
        if (opts.knowsDevanagari && prev.track === 'devanagari') continue
        return isLessonPassed(p, prev.id)
    }

    return true
}

/** The next lesson to recommend: the first unlocked lesson not yet passed. */
export const nextLesson = (p: Progress, opts: UnlockOptions): Lesson | null => {
    for (const lesson of LESSONS) {
        if (opts.knowsDevanagari && lesson.track === 'devanagari') continue
        if (isLessonPassed(p, lesson.id)) continue
        if (isLessonUnlocked(p, lesson.id, opts)) return lesson
    }

    return null
}

export const ACHIEVEMENTS: Record<string, Bi & { sa: string; icon: string }> = {
    'first-lesson': { sa: 'प्रथमः पाठः', hi: 'पहला पाठ पूरा', en: 'First lesson complete', icon: 'sparkles' },
    'first-sentence': { sa: 'प्रथमं वाक्यम्', hi: 'पहला संस्कृत वाक्य', en: 'First Sanskrit sentence', icon: 'message-circle' },
    'devanagari': { sa: 'लिपिज्ञः', hi: 'देवनागरी पूरी', en: 'Devanagari complete', icon: 'pen-line' },
    'first-story': { sa: 'कथापाठकः', hi: 'पहली कहानी पढ़ी', en: 'First story read', icon: 'book-open' },
    'level-4': { sa: 'प्रथमसोपानम्', hi: 'पहला चरण पूरा', en: 'First stage finished', icon: 'graduation-cap' },
    perfect: { sa: 'सर्वोत्तमम्', hi: 'पूरे अंक', en: 'Perfect score', icon: 'star' },
    'streak-3': { sa: 'त्रिदिनम्', hi: '3 दिन लगातार', en: '3-day streak', icon: 'flame' },
    'streak-7': { sa: 'सप्ताहः', hi: '7 दिन लगातार', en: '7-day streak', icon: 'flame' },
    'xp-100': { sa: 'शतम्', hi: '100 XP', en: '100 XP', icon: 'zap' },
    'xp-500': { sa: 'पञ्चशतम्', hi: '500 XP', en: '500 XP', icon: 'zap' },
    'game-player': { sa: 'क्रीडकः', hi: 'पहला खेल', en: 'First game played', icon: 'gift' }
}

export const evaluateAchievements = (p: Progress, lastScore?: number): string[] => {
    const set = new Set(p.achievements)
    const passedCount = Object.values(p.lessons).filter(l => l.passed).length

    if (passedCount >= 1) set.add('first-lesson')
    if (isLessonPassed(p, 'l15-first-sentence')) set.add('first-sentence')
    if (['l02-svara', 'l03-vyanjana', 'l04-matra', 'l05-pathanam'].every(id => isLessonPassed(p, id))) set.add('devanagari')
    if (p.storiesRead.length > 0 || isLessonPassed(p, 'l20-first-story')) set.add('first-story')
    if (isLessonPassed(p, 'l20-first-story')) set.add('level-4')
    if (lastScore !== undefined && lastScore >= 0.999) set.add('perfect')
    if (p.streak.best >= 3) set.add('streak-3')
    if (p.streak.best >= 7) set.add('streak-7')
    if (p.xp >= 100) set.add('xp-100')
    if (p.xp >= 500) set.add('xp-500')
    if (Object.keys(p.games).length > 0) set.add('game-player')

    return Array.from(set)
}

export interface WeakTopic {
    id: string
    label: Bi
    count: number
    practiceHref: string
}

const TOPIC_RULES: Array<{ match: (key: string) => boolean; id: string; label: Bi; href: string }> = [
    { match: k => /^vacana:(dvi>bahu|bahu>dvi)$/.test(k), id: 'dvi-bahu', label: { hi: 'द्विवचन बनाम बहुवचन', en: 'Dual vs plural' }, href: '/sanskrit/practice/dvivacana' },
    { match: k => /^vacana:(eka>dvi|dvi>eka)$/.test(k), id: 'eka-dvi', label: { hi: 'एकवचन बनाम द्विवचन', en: 'Singular vs dual' }, href: '/sanskrit/practice/dvivacana' },
    { match: k => /^vacana:(eka>bahu|bahu>eka)$/.test(k), id: 'eka-bahu', label: { hi: 'एकवचन बनाम बहुवचन', en: 'Singular vs plural' }, href: '/sanskrit/practice/dhatu-challenge' },
    { match: k => k.startsWith('purusha:uttama>'), id: 'uttama', label: { hi: 'उत्तम पुरुष (अहम्, वयम्)', en: 'उत्तम पुरुष (I, we)' }, href: '/sanskrit/practice/dhatu-challenge' },
    { match: k => k.startsWith('purusha:madhyama>'), id: 'madhyama', label: { hi: 'मध्यम पुरुष (त्वम्, यूयम्)', en: 'मध्यम पुरुष (you)' }, href: '/sanskrit/practice/dhatu-challenge' },
    { match: k => k.startsWith('purusha:prathama>'), id: 'prathama', label: { hi: 'प्रथम पुरुष (सः, ते)', en: 'प्रथम पुरुष (he, they)' }, href: '/sanskrit/practice/dhatu-challenge' },
    { match: k => k.startsWith('lakara:'), id: 'lakara', label: { hi: 'लकार पहचान', en: 'Recognising lakāras' }, href: '/sanskrit/practice/lakara-pehchan' },
    { match: k => k.startsWith('vibhakti:'), id: 'vibhakti', label: { hi: 'विभक्ति चयन', en: 'Choosing the right case' }, href: '/sanskrit/practice/vibhakti-chayan' },
    { match: k => k.startsWith('gender:'), id: 'gender', label: { hi: 'लिङ्ग', en: 'Gender agreement' }, href: '/sanskrit/practice/shabdarupa-sadhana' }
]

const SKILL_PRACTICE: Partial<Record<SkillId, string>> = {
    devanagari: '/sanskrit/practice/akshara-nirman',
    listening: '/sanskrit/practice/shravan',
    vocabulary: '/sanskrit/practice/review',
    conjugation: '/sanskrit/practice/dhatu-challenge',
    'verb-production': '/sanskrit/practice/dhatu-challenge',
    lakara: '/sanskrit/practice/lakara-pehchan',
    declension: '/sanskrit/practice/shabdarupa-sadhana',
    vibhakti: '/sanskrit/practice/vibhakti-chayan',
    sentence: '/sanskrit/practice/vakya-nirmata',
    sandhi: '/sanskrit/practice/sandhi-nirmata',
    comprehension: '/sanskrit/practice/katha-krama',
    reading: '/sanskrit/stories'
}

export const weakTopics = (p: Progress, skillLabel: (s: SkillId) => Bi): WeakTopic[] => {
    const topics = new Map<string, WeakTopic>()

    for (const [key, count] of Object.entries(p.confusions)) {
        const rule = TOPIC_RULES.find(r => r.match(key))
        if (!rule) continue
        const t = topics.get(rule.id) ?? { id: rule.id, label: rule.label, count: 0, practiceHref: rule.href }
        t.count += count
        topics.set(rule.id, t)
    }

    for (const [skill, stats] of Object.entries(p.skills) as Array<[SkillId, { correct: number; total: number }]>) {
        if (stats.total < 5) continue
        const accuracy = stats.correct / stats.total
        if (accuracy >= 0.7) continue
        const wrong = stats.total - stats.correct
        topics.set(`skill:${skill}`, { id: `skill:${skill}`, label: skillLabel(skill), count: wrong, practiceHref: SKILL_PRACTICE[skill] ?? '/sanskrit/practice' })
    }

    return Array.from(topics.values()).filter(t => t.count >= 2).sort((a, b) => b.count - a.count).slice(0, 5)
}

export const dueItems = (p: Progress, now = new Date()): string[] => {
    const today = toDay(now)

    return Object.entries(p.items)
        .filter(([, s]) => s.seen > 0 && s.due <= today)
        .sort((a, b) => (a[1].due < b[1].due ? -1 : 1))
        .map(([id]) => id)
}

export const masteryCounts = (p: Progress): Record<MasteryState, number> => {
    const counts: Record<MasteryState, number> = { new: 0, learning: 0, 'needs-practice': 0, strong: 0, mastered: 0 }
    for (const item of Object.values(p.items)) counts[masteryOf(item)]++
    return counts
}

export const pushRecentWord = (p: Progress, wordId: string): Progress => {
    const recentWords = [wordId, ...p.recentWords.filter(w => w !== wordId)].slice(0, 30)
    return { ...p, recentWords }
}

/** The newest REVISION_LOG_CAP marks, newest first (the same input always keeps the same marks). */
export const pruneRevisionLog = (log: Record<string, RevisionMark>): Record<string, RevisionMark> => {
    const entries = Object.entries(log)

    if (entries.length <= REVISION_LOG_CAP) return log

    entries.sort(([ka, a], [kb, b]) => (a.at < b.at ? 1 : a.at > b.at ? -1 : ka < kb ? -1 : ka > kb ? 1 : 0))

    return Object.fromEntries(entries.slice(0, REVISION_LOG_CAP))
}

export const toggleRevision = (p: Progress, key: string, now = new Date()): Progress => {
    const has = p.revision.includes(key)
    const revisionLog = pruneRevisionLog({ ...p.revisionLog, [key]: { on: !has, at: now.toISOString() } })

    return { ...p, revision: has ? p.revision.filter(k => k !== key) : [key, ...p.revision].slice(0, 200), revisionLog }
}

/** The XP counter of one device for one account (or the guest), so two devices' XP add up instead of overwriting. */
export const xpKey = (device: string, owner: string | null): string => `${device}:${owner ?? 'guest'}`

/** Count XP gained (or lost) between two copies on the given counter. */
export const countXp = (prev: Progress, next: Progress, key: string): Progress => {
    const delta = next.xp - prev.xp

    if (!delta || !key) return next

    return { ...next, xpBy: { ...next.xpBy, [key]: Math.max(0, (next.xpBy[key] ?? 0) + delta) } }
}

export const recordGame = (p: Progress, gameId: string, score: number, total: number, now = new Date()): { progress: Progress; xp: number; levelUp: boolean } => {
    const prev = p.games[gameId] ?? { plays: 0, best: 0, level: 1, last: now.toISOString() }
    const ratio = total > 0 ? score / total : 0
    const levelUp = ratio >= 0.8 && prev.level < 3
    const xp = score * 2 + (ratio >= 0.8 ? 5 : 0)

    let next: Progress = {
        ...p,
        games: {
            ...p.games,
            [gameId]: { plays: prev.plays + 1, best: Math.max(prev.best, score), level: levelUp ? prev.level + 1 : prev.level, last: now.toISOString() }
        }
    }

    next = addXp(next, xp, now)
    next = { ...next, achievements: evaluateAchievements(next) }

    return { progress: next, xp, levelUp }
}

export const markStoryRead = (p: Progress, storyId: string): Progress => {
    if (p.storiesRead.includes(storyId)) return p
    const next = addXp({ ...p, storiesRead: [...p.storiesRead, storyId] }, 10)
    return { ...next, achievements: evaluateAchievements(next) }
}

/** Defensive merge for stored data from older versions or hand edits. */
export const reviveProgress = (raw: unknown): Progress => {
    const base = emptyProgress()
    if (!raw || typeof raw !== 'object') return base
    const r = raw as Partial<Progress>

    return {
        ...base,
        ...r,
        version: 1,
        streak: { ...base.streak, ...(r.streak ?? {}) },
        daily: r.daily ?? {},
        lessons: r.lessons ?? {},
        items: r.items ?? {},
        skills: r.skills ?? {},
        confusions: r.confusions ?? {},
        recentWords: Array.isArray(r.recentWords) ? r.recentWords : [],
        revision: Array.isArray(r.revision) ? r.revision : [],
        revisionLog: r.revisionLog && typeof r.revisionLog === 'object' ? r.revisionLog : {},
        xpBy: r.xpBy && typeof r.xpBy === 'object' ? r.xpBy : {},
        lastLessonAt: typeof r.lastLessonAt === 'string' ? r.lastLessonAt : null,
        games: r.games ?? {},
        achievements: Array.isArray(r.achievements) ? r.achievements : [],
        history: Array.isArray(r.history) ? r.history : [],
        storiesRead: Array.isArray(r.storiesRead) ? r.storiesRead : []
    }
}
